import { useState } from 'react';
import { authAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { Card, Button, Input, Select, Badge, SectionHeader } from '../components/ui';
import toast from 'react-hot-toast';
import { User, Lock, Shield, Mail, Smartphone } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

export default function Settings() {
  const { user, updateUser, refreshUser, logout } = useAuth();

  // Profile form
  const [profile, setProfile] = useState({
    name:        user?.name        || '',
    phone:       user?.phone       || '',
    country:     user?.country     || 'Uganda',
    currency:    user?.currency    || 'UGX',
    ispIdentity: user?.ispIdentity || '',
  });
  const [profileLoading, setProfileLoading] = useState(false);

  // Password form
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Email verification
  const [verifyLoading, setVerifyLoading] = useState(false);

  // 2FA state
  const [tfaStep, setTfaStep] = useState('idle'); // idle | setup | verify | disable
  const [tfaSecret, setTfaSecret] = useState('');
  const [tfaQr, setTfaQr] = useState('');
  const [tfaToken, setTfaToken] = useState('');
  const [tfaPassword, setTfaPassword] = useState('');
  const [tfaLoading, setTfaLoading] = useState(false);

  const setP = (k) => (e) => setProfile(f => ({ ...f, [k]: e.target.value }));
  const setW = (k) => (e) => setPasswords(f => ({ ...f, [k]: e.target.value }));

  // ── Profile ───────────────────────────────────────────────────
  const handleProfileSave = async (e) => {
    e.preventDefault();
    if (!profile.name) { toast.error('Name is required'); return; }
    setProfileLoading(true);
    try {
      const res = await authAPI.updateProfile({
        name: profile.name, phone: profile.phone,
        country: profile.country, currency: profile.currency,
        ispIdentity: profile.ispIdentity,
      });
      updateUser(res.user || profile);
      toast.success('Profile updated!');
    } catch (err) {
      toast.error(err.message || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  // ── Password ──────────────────────────────────────────────────
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwords.newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (passwords.newPassword !== passwords.confirmPassword) { toast.error('Passwords do not match'); return; }
    setPasswordLoading(true);
    try {
      await authAPI.changePassword({ currentPassword: passwords.currentPassword, newPassword: passwords.newPassword });
      toast.success('Password changed! Logging out...');
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(logout, 2000);
    } catch (err) {
      toast.error(err.message || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  // ── Email verification ────────────────────────────────────────
  const handleSendVerification = async () => {
    setVerifyLoading(true);
    try {
      await authAPI.sendVerification();
      toast.success('Verification email sent! Check your inbox.');
    } catch (err) {
      toast.error(err.message || 'Failed to send verification email');
    } finally {
      setVerifyLoading(false);
    }
  };

  // ── 2FA Setup ─────────────────────────────────────────────────
  const handleSetup2FA = async () => {
    setTfaLoading(true);
    try {
      const res = await authAPI.setup2fa();
      setTfaSecret(res.secret || '');
      setTfaQr(res.otpauthUrl || '');
      setTfaStep('setup');
    } catch (err) {
      toast.error(err.message || 'Failed to setup 2FA');
    } finally {
      setTfaLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!tfaToken) { toast.error('Enter the 6-digit code from your authenticator app'); return; }
    setTfaLoading(true);
    try {
      await authAPI.verify2fa(tfaToken);
      toast.success('2FA enabled successfully!');
      setTfaStep('idle');
      setTfaToken('');
      await refreshUser();
    } catch (err) {
      toast.error(err.message || 'Invalid code. Try again.');
    } finally {
      setTfaLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!tfaPassword) { toast.error('Enter your password to disable 2FA'); return; }
    setTfaLoading(true);
    try {
      await authAPI.disable2fa(tfaPassword);
      toast.success('2FA disabled.');
      setTfaStep('idle');
      setTfaPassword('');
      await refreshUser();
    } catch (err) {
      toast.error(err.message || 'Failed to disable 2FA');
    } finally {
      setTfaLoading(false);
    }
  };

  const is2faEnabled = user?.twoFactorEnabled;

  return (
    <div className="space-y-6 animate-slide-up max-w-2xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-black text-text-primary tracking-tight">Settings</h1>
        <p className="text-text-secondary text-sm mt-1">Manage your account profile and security settings.</p>
      </div>

      {/* Account summary */}
      <Card>
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center font-black text-2xl text-white flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #00A8DB, #8B5CF6)' }}>
            {user?.name?.[0] || 'U'}
          </div>
          <div className="flex-1">
            <p className="text-text-primary font-bold text-lg">{user?.name}</p>
            <p className="text-text-muted text-sm">{user?.email}</p>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <Badge label={user?.role?.replace('_', ' ') || 'Client'} color="cyan" />
              <Badge label={user?.kyc?.status === 'approved' ? 'KYC Verified' : 'KYC Pending'} color={user?.kyc?.status === 'approved' ? 'green' : 'amber'} />
              {user?.emailVerified && <Badge label="Email Verified" color="green" />}
              {is2faEnabled && <Badge label="2FA Active" color="cyan" />}
            </div>
          </div>
        </div>

        {/* Last Login Info */}
        {user?.lastLogin && (
          <div className="mt-4 pt-4 border-t border-border/40">
            <div className="flex items-center justify-between text-xs">
              <span className="text-text-muted font-semibold uppercase tracking-wider">Last Login</span>
              <span className="text-text-secondary">
                {new Date(user.lastLogin).toLocaleString()}
                {user?.lastLoginIp && <span className="ml-2 text-text-muted">· {user.lastLoginIp}</span>}
              </span>
            </div>
          </div>
        )}
      </Card>

      {/* Profile settings */}
      <Card>
        <SectionHeader title={<span className="flex items-center gap-2"><User className="w-4 h-4 text-cyan" /> Profile Information</span>} />
        <form onSubmit={handleProfileSave} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Input label="Full Name" type="text" placeholder="John Mbula" value={profile.name} onChange={setP('name')} required />
            <Input label="Phone Number" type="tel" placeholder="+256 77X XXX XXX" value={profile.phone} onChange={setP('phone')} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Select label="Country" value={profile.country} onChange={setP('country')}>
              <option value="Uganda">Uganda</option>
              <option value="Kenya">Kenya</option>
              <option value="Tanzania">Tanzania</option>
              <option value="Rwanda">Rwanda</option>
              <option value="Other">Other</option>
            </Select>
            <Select label="Preferred Currency" value={profile.currency} onChange={setP('currency')}>
              <option value="UGX">UGX — Ugandan Shilling</option>
              <option value="USD">USD — US Dollar</option>
              <option value="KES">KES — Kenyan Shilling</option>
            </Select>
          </div>
          <Input label="DWIFI username/ticket number" type="text" placeholder="e.g. ORANGE1-1234"
            value={profile.ispIdentity} onChange={setP('ispIdentity')} />
          <div className="bg-bg-panel border border-border rounded-xl px-4 py-3">
            <p className="text-text-muted text-xs font-bold uppercase tracking-wider mb-1">Email Address</p>
            <p className="text-text-primary text-sm">{user?.email}</p>
            <p className="text-text-muted text-xs mt-1">Email cannot be changed. Contact support if needed.</p>
          </div>
          <Button type="submit" loading={profileLoading}>Save Profile</Button>
        </form>
      </Card>

      {/* Email Verification */}
      <Card>
        <SectionHeader title={<span className="flex items-center gap-2"><Mail className="w-4 h-4 text-cyan" /> Email Verification</span>} />
        {user?.emailVerified ? (
          <div className="flex items-center gap-3 p-3 bg-green/10 border border-green/25 rounded-xl">
            <div className="w-8 h-8 rounded-full bg-green/20 flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <p className="text-green-400 font-semibold text-sm">Email Verified</p>
              <p className="text-text-muted text-xs">Your email address is verified.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-amber/10 border border-amber/25 rounded-xl">
              <div className="w-8 h-8 rounded-full bg-amber/20 flex items-center justify-center flex-shrink-0">
                <Mail className="w-4 h-4 text-amber-400" />
              </div>
              <div className="flex-1">
                <p className="text-amber-400 font-semibold text-sm">Email Not Verified</p>
                <p className="text-text-muted text-xs">Verify your email to improve account security.</p>
              </div>
            </div>
            <Button variant="outline" size="sm" loading={verifyLoading} onClick={handleSendVerification}>
              <Mail className="w-4 h-4" />
              Send Verification Email
            </Button>
          </div>
        )}
      </Card>

      {/* 2FA */}
      <Card>
        <SectionHeader title={<span className="flex items-center gap-2"><Smartphone className="w-4 h-4 text-cyan" /> Two-Factor Authentication</span>} />

        {tfaStep === 'idle' && (
          <div className="space-y-4">
            <div className={`flex items-center gap-3 p-3 rounded-xl border ${is2faEnabled ? 'bg-green/10 border-green/25' : 'bg-bg-panel border-border'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${is2faEnabled ? 'bg-green/20' : 'bg-bg'}`}>
                <Shield className={`w-4 h-4 ${is2faEnabled ? 'text-green-400' : 'text-text-muted'}`} />
              </div>
              <div className="flex-1">
                <p className={`font-semibold text-sm ${is2faEnabled ? 'text-green-400' : 'text-text-primary'}`}>
                  2FA is {is2faEnabled ? 'Enabled' : 'Disabled'}
                </p>
                <p className="text-text-muted text-xs">
                  {is2faEnabled
                    ? 'Your account is protected with a time-based authenticator.'
                    : 'Add an extra layer of security with Google Authenticator or Authy.'}
                </p>
              </div>
            </div>

            {is2faEnabled ? (
              <Button variant="danger" size="sm" onClick={() => setTfaStep('disable')}>
                Disable 2FA
              </Button>
            ) : (
              <Button variant="outline" size="sm" loading={tfaLoading} onClick={handleSetup2FA}>
                <Smartphone className="w-4 h-4" />
                Enable 2FA
              </Button>
            )}
          </div>
        )}

        {tfaStep === 'setup' && (
          <div className="space-y-4">
            <p className="text-text-secondary text-sm">Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.):</p>
            {tfaQr && (
              <div className="flex justify-center">
                <div className="bg-white p-4 rounded-xl inline-block">
                  <QRCodeSVG value={tfaQr} size={180} />
                </div>
              </div>
            )}
            {tfaSecret && (
              <div className="bg-bg-panel border border-border rounded-xl p-3">
                <p className="text-text-muted text-xs font-semibold uppercase tracking-wider mb-1">Manual Code</p>
                <p className="text-text-primary font-mono text-sm break-all">{tfaSecret}</p>
              </div>
            )}
            <Input
              label="Enter 6-digit code from your app"
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              value={tfaToken}
              onChange={(e) => setTfaToken(e.target.value.replace(/\D/g, ''))}
            />
            <div className="flex gap-3">
              <Button loading={tfaLoading} onClick={handleVerify2FA} className="flex-1">Verify & Enable</Button>
              <Button variant="outline" onClick={() => { setTfaStep('idle'); setTfaToken(''); }}>Cancel</Button>
            </div>
          </div>
        )}

        {tfaStep === 'disable' && (
          <div className="space-y-4">
            <div className="bg-red-500/10 border border-red-500/25 rounded-xl p-3">
              <p className="text-red-400 text-sm font-semibold">Disable 2FA?</p>
              <p className="text-text-muted text-xs mt-1">This will reduce the security of your account. Enter your password to confirm.</p>
            </div>
            <Input
              label="Current Password"
              type="password"
              placeholder="Enter your password"
              value={tfaPassword}
              onChange={(e) => setTfaPassword(e.target.value)}
            />
            <div className="flex gap-3">
              <Button variant="danger" loading={tfaLoading} onClick={handleDisable2FA} className="flex-1">Confirm Disable</Button>
              <Button variant="outline" onClick={() => { setTfaStep('idle'); setTfaPassword(''); }}>Cancel</Button>
            </div>
          </div>
        )}
      </Card>

      {/* Password Change */}
      <Card>
        <SectionHeader title={<span className="flex items-center gap-2"><Lock className="w-4 h-4 text-cyan" /> Change Password</span>} />
        <form onSubmit={handlePasswordChange} className="space-y-4">
          <Input label="Current Password" type="password" placeholder="Enter current password" value={passwords.currentPassword} onChange={setW('currentPassword')} required />
          <Input label="New Password" type="password" placeholder="Minimum 6 characters" value={passwords.newPassword} onChange={setW('newPassword')} required />
          <Input label="Confirm New Password" type="password" placeholder="Repeat new password" value={passwords.confirmPassword} onChange={setW('confirmPassword')}
            error={passwords.confirmPassword && passwords.newPassword !== passwords.confirmPassword ? 'Passwords do not match' : ''} required />
          <div className="bg-amber/10 border border-amber/25 rounded-xl p-3 text-amber text-xs">
            ⚠ You will be logged out automatically after changing your password.
          </div>
          <Button type="submit" variant="danger" loading={passwordLoading}>Change Password</Button>
        </form>
      </Card>

      {/* Account Info */}
      <Card>
        <SectionHeader title={<span className="flex items-center gap-2"><Shield className="w-4 h-4 text-cyan" /> Account Information</span>} />
        <div className="space-y-0">
          {[
            ['Account ID',    user?._id || '—',                                                      'font-mono text-xs break-all'],
            ['Member Since',  user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—', ''],
            ['Account Role',  user?.role?.replace('_', ' ') || 'client',                             'capitalize'],
            ['Email Status',  user?.emailVerified ? 'Verified ✓' : 'Not Verified', user?.emailVerified ? 'text-green-400' : 'text-amber-400'],
            ['2FA Status',    is2faEnabled ? 'Enabled ✓' : 'Disabled',             is2faEnabled ? 'text-green-400' : 'text-text-muted'],
          ].map(([label, value, extraClass]) => (
            <div key={label} className="flex items-start justify-between py-3 border-b border-border/40 last:border-0">
              <span className="text-text-muted text-xs font-semibold uppercase tracking-wider pt-0.5 flex-shrink-0 mr-4">{label}</span>
              <span className={`text-text-secondary text-sm text-right ${extraClass}`}>{String(value)}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Danger Zone */}
      <Card className="border-red/30">
        <SectionHeader title="Danger Zone" />
        <div className="flex items-center justify-between">
          <div>
            <p className="text-text-primary text-sm font-semibold">Log Out</p>
            <p className="text-text-muted text-xs">Sign out of your account on this device</p>
          </div>
          <Button variant="danger" size="sm" onClick={logout}>Log Out</Button>
        </div>
      </Card>
    </div>
  );
}
